import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ConfigService } from '../../shared-services/config.service';
import { MatMenuModule } from '@angular/material/menu';
import { debounceTime, EMPTY, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { formatDisplayText } from '../../utils/text-case.utils';
import { SelfServiceFormulaService } from '../../self-service-visualization/shared/self-service-formula.service';
import { applyDrillFormulasToTree, type DrillFormulaMeta } from '../unified-table-matrix-chart/drilldown-client-tree.utils';

/** One edited cell in the drill-down tree, addressed by the node's ancestor
 *  path (root → the node itself) so the backend can locate the record without
 *  a rowId (the tree has none). A MEASURE edit carries a numeric `value`; a
 *  LABEL edit (tree-column rename) carries the new string, `field` is the
 *  level's dimension field, and `path` ends in the NEW name — `oldValue` holds
 *  the original for the backend's lookup. */
export interface DrillDownCellChange {
  path: string[];
  name: string;
  level: number;
  field: string;
  value: number | string | null;
  oldValue: unknown;
  /** The source row values for this edited node (for example uuid). */
  rowValues?: Record<string, unknown>;
  /** 'text' for a free-text attribute column (value is a string, nothing to
   *  re-sum); absent = numeric measure. */
  kind?: 'text';
}

/** Payload of the drill-down's `save` output — all pending cell edits. */
export interface DrillDownSaveEvent {
  widgetId: string;
  changes: DrillDownCellChange[];
}

@Component({
  selector: 'lib-drill-down',
  imports: [
    NgxDatatableModule,
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './drill-down.component.html',
  styleUrl: './drill-down.component.scss',
})
export class DrillDownComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  grandTotalRow: any;
  filter: any;
  previouSelection = '';
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }
  protected _widgetId!: string;

  /** Per-row CSS hook for ngx-datatable: tags every data row with its
   *  hierarchy depth (`dd-level-1`, `dd-level-2`, …) so the stylesheet can
   *  tint a whole level — level 2 gets the light grey band. Total rows are
   *  left untagged so they keep their own styling. */
  rowClass = (row: any): Record<string, boolean> => {
    if (!row || row.isTotal) return {};
    const level = Number(row.level);
    if (!Number.isFinite(level) || level <= 0) return {};
    const classes: Record<string, boolean> = { [`dd-level-${level}`]: true };
    // Level-2 colour grouping: every distinct group gets a palette slot
    // (`dd-cgroup-<n>`, wrapping past the palette size) painted by the SCSS.
    const group = this.level2GroupIndex(row);
    if (group !== null) classes[`dd-cgroup-${group % DrillDownComponent.LEVEL2_PALETTE_SIZE}`] = true;
    return classes;
  };

  // ── Level-2 colour grouping (config-driven via `data`) ──────────────────────
  // `level2ColorBy`: 'parent' (EVERY level-2 row gets the SAME tint — one
  // uniform band beneath each level-1 parent, palette slot 0) | 'label'
  // (identical level-2 labels share a colour, distinct labels differ).
  // Anything else / missing = off (the plain grey level-2 text applies).
  static readonly LEVEL2_PALETTE_SIZE = 12;
  get level2ColorBy(): 'parent' | 'label' | null {
    const mode = String(this.data?.level2ColorBy || '').trim();
    return mode === 'parent' || mode === 'label' ? mode : null;
  }
  /** Distinct group key → palette slot, assigned in tree order over the WHOLE
   *  tree (not just the expanded rows) so a group keeps its colour no matter
   *  which parents are open. STICKY for the life of the component: a re-flatten
   *  (sort, expand, edit, filter recompute) only appends keys it has not seen,
   *  so "QSK19" stays the same tint however the rows get reordered. The map is
   *  reset only when the grouping MODE changes (parent ↔ label keys differ). */
  private level2Groups = new Map<string, number>();
  private level2GroupsMode: 'parent' | 'label' | null = null;
  private level2GroupKey(row: any): string | null {
    const mode = this.level2ColorBy;
    if (!mode || !row || row.isTotal || Number(row.level) !== 2) return null;
    // One shared key → every level-2 row lands in the same (first) palette slot.
    if (mode === 'parent') return '';
    return String(row[this.nameProp] ?? '').trim();
  }
  private level2GroupIndex(row: any): number | null {
    const key = this.level2GroupKey(row);
    if (key === null) return null;
    const idx = this.level2Groups.get(key);
    return idx === undefined ? null : idx;
  }
  private rebuildLevel2Groups(data: any[]): void {
    const mode = this.level2ColorBy;
    if (mode !== this.level2GroupsMode) {
      this.level2Groups = new Map();
      this.level2GroupsMode = mode;
    }
    if (!mode) return;
    (data || []).forEach((parent) => {
      (parent?.children || []).forEach((child: any) => {
        // `parent` back-links are only set for flattened rows; wire it
        // temporarily so the key derivation sees the level-1 node.
        const key = this.level2GroupKey({ ...child, parent });
        if (key !== null && !this.level2Groups.has(key)) this.level2Groups.set(key, this.level2Groups.size);
      });
    });
  }

  /** Row property holding each node's display text — defaults to `name`
   *  (the legacy shape); pass `data.nameProp = 'label'` for consumers whose
   *  backend uses that key instead (e.g. the DVM Layout Builder). */
  get nameProp(): string {
    return this.data?.nameProp || 'name';
  }

  // ── Freeze panes (Format popup → Columns group, forwarded by the Table &
  //    Matrix host as data.freezeColumnCount / data.freezeRowCount) ──────────
  /** How many of the first VISIBLE columns are pinned to the left while the
   *  body scrolls sideways. `data.freezeColumnCount` (1–6), or 1 for the legacy
   *  boolean `data.freezeFirstColumn`. Counted on the visible list, so a
   *  collapsed level column is skipped rather than "frozen" while hidden. */
  get frozenColumnCount(): number {
    const n = Number(this.data?.freezeColumnCount);
    if (Number.isFinite(n) && n > 0) return Math.min(6, Math.floor(n));
    return this.data?.freezeFirstColumn === true ? 1 : 0;
  }
  isFrozenColumn(index: number): boolean {
    return index < this.frozenColumnCount;
  }
  /** Sticky `left` (px) for the Total-row / band cell at a visible column
   *  index: the measured widths of the frozen columns before it, so the pinned
   *  cells below/above the header land exactly on ngx-datatable's own frozen
   *  cells (same source as totalCellStyle). */
  frozenLeftOffset(index: number): number {
    let left = 0;
    for (let i = 0; i < index && i < this.frozenColumnCount; i++) {
      const measured = this.columnWidths[i];
      left += measured != null && measured > 0 ? measured : (this.visibleColumns[i]?.width || 0);
    }
    return left;
  }
  /** How many of the top rendered rows stay pinned under the header while the
   *  body scrolls (`data.freezeRowCount`, capped at 10 — the SCSS generates
   *  one sticky rule per slot). Whatever rows currently sit in the first N
   *  positions of the flattened tree are the pinned ones. */
  get frozenRowCount(): number {
    const n = Number(this.data?.freezeRowCount);
    return Number.isFinite(n) && n > 0 ? Math.min(10, Math.floor(n)) : 0;
  }
  /** Host class for the datatable: `dd-freeze-rows-<n>` selects the nth-child
   *  sticky rules in the stylesheet (no per-row bookkeeping in `rowClass`). */
  get freezeRowsClass(): string {
    const n = this.frozenRowCount;
    return n > 0 ? `dd-freeze-rows dd-freeze-rows-${n}` : '';
  }

  /** Round-off precision for number cells + the Total row. Pass
   *  `data.decimals` (the Format popup's "Decimal places") to override the
   *  legacy whole-number default. */
  get numberDecimals(): number {
    const raw = Number(this.data?.decimals);
    return Number.isFinite(raw) ? Math.max(0, Math.min(6, Math.floor(raw))) : 0;
  }

  /** Angular number-pipe digitsInfo for the number cells (e.g. "1.2-2"). */
  get numberDigitsInfo(): string {
    const d = this.numberDecimals;
    return `1.${d}-${d}`;
  }

  /** Number SCALE for the number cells + Total row — the Format popup's
   *  "Number format" (`data.numberFormat`, or the older `data.numberScale`):
   *  none | compact (auto K/M/B) | thousands | millions | billions. The
   *  upper-case preset spellings the KPI/bar tiles use are accepted too. */
  get numberScale(): 'none' | 'compact' | 'thousands' | 'millions' | 'billions' {
    const raw = String(this.data?.numberScale ?? this.data?.numberFormat ?? 'none').toLowerCase();
    if (raw === 'compact') return 'compact';
    if (raw === 'thousands' || raw === 'scale_thousands') return 'thousands';
    if (raw === 'millions') return 'millions';
    if (raw === 'billions') return 'billions';
    return 'none';
  }

  /** Display text of one PERCENTAGE cell, INCLUDING the trailing "%" — blank
   *  input renders truly empty (no bare "%"), matching the number pipe's
   *  behavior for a blank number cell. `column.percentScale === 'fraction'`
   *  means the raw value is a 0-1 ratio (0.05) and must be ×100'd first to
   *  read "5.0%"; otherwise the raw value is already in percentage points
   *  (4.6) and is used as-is. `column.hideNegative` blanks the cell entirely
   *  once the (scaled) value is negative — e.g. SSA % showing nothing instead
   *  of "-8.6%" — without touching the Total/Grand Total row's own math. */
  formatPercentCell(value: unknown, column?: { percentScale?: string; hideNegative?: boolean; decimals?: number }): string {
    if (value === null || value === undefined || value === '') return '';
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(n)) return '';
    const scaled = column?.percentScale === 'fraction' ? n * 100 : n;
    if (column?.hideNegative && scaled < 0) return '';
    const decimals = typeof column?.decimals === 'number'
      ? Math.max(0, Math.min(6, Math.floor(column.decimals)))
      : this.numberDecimals;
    return `${scaled.toFixed(decimals)}%`;
  }

  /** Grand totals are pre-scaled by setTotal, unlike regular percentage cells.
   *  Keep that representation intact while applying the same negative-value
   *  visibility rule used by the body rows. */
  formatTotalPercentCell(value: unknown, column?: { hideNegative?: boolean; decimals?: number }): string {
    if (value === null || value === undefined || value === '') return '';
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(n) || (column?.hideNegative && n < 0)) return '';
    const decimals = typeof column?.decimals === 'number'
      ? Math.max(0, Math.min(6, Math.floor(column.decimals)))
      : this.numberDecimals;
    return `${n.toFixed(decimals)}%`;
  }

  /** Display text of one NUMBER cell (and the Total row): the value rounded
   *  to `numberDecimals` places (fixed — "1,234.0" at 1, "1,234" at 0),
   *  thousands-grouped, and scaled per `numberScale` with a K / M / B suffix
   *  ("16.5M"). Non-numeric / blank input renders empty, like the number pipe. */
  formatNumberCell(value: unknown, column?: { decimals?: number }): string {
    if (value === null || value === undefined || value === '') return '';
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(n)) return '';
    const decimals = typeof column?.decimals === 'number' ? Math.max(0, Math.min(6, Math.floor(column.decimals))) : this.numberDecimals;
    let scaled = n;
    let suffix = '';
    const abs = Math.abs(n);
    switch (this.numberScale) {
      case 'thousands': scaled = n / 1e3; suffix = 'K'; break;
      case 'millions': scaled = n / 1e6; suffix = 'M'; break;
      case 'billions': scaled = n / 1e9; suffix = 'B'; break;
      case 'compact':
        if (abs >= 1e9) { scaled = n / 1e9; suffix = 'B'; }
        else if (abs >= 1e6) { scaled = n / 1e6; suffix = 'M'; }
        else if (abs >= 1e3) { scaled = n / 1e3; suffix = 'K'; }
        break;
      default: break;
    }
    // Round half away from zero (toFixed alone rounds 0.5 binary-inexactly).
    const factor = Math.pow(10, decimals);
    const rounded = Math.sign(scaled) * Math.round(Math.abs(scaled) * factor + Number.EPSILON) / factor;
    const body = (Object.is(rounded, -0) ? 0 : rounded).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `${body}${suffix}`;
  }

  // ── Visual / reporting capabilities (all config-driven via `data`) ──────────
  // Opt-IN, matching every other toolbar flag below. It used to be opt-out
  // (`!== false`), which put Expand all / Collapse all on every drill-down table
  // whether or not anyone asked for them.
  get enableExpandCollapseAll(): boolean { return this.data?.enableExpandCollapseAll === true; }
  get enableExport(): boolean { return this.data?.enableExport === true; }
  get enableExcelExport(): boolean { return this.data?.enableExcelExport === true; }
  get enablePdfExport(): boolean { return this.data?.enablePdfExport === true; }
  get enablePrint(): boolean { return this.data?.enablePrint === true; }
  get enableSavedViews(): boolean { return this.data?.enableSavedViews === true; }
  get showToolbar(): boolean {
    return this.enableExpandCollapseAll || this.enableExport || this.enableExcelExport
      || this.enablePdfExport || this.enablePrint || this.enableSavedViews;
  }
  /** Conditional-format / data-bar / heatmap rules, keyed by column prop. */
  get visualRules(): Record<string, any> { return (this.data?.visualRules as Record<string, any>) || {}; }
  private get titleText(): string { return String(this.data?.title || 'drill-down'); }

  /** The min/max of a numeric column across the CURRENT rows — memoized per
   *  render so data-bars / heat-maps scale to the visible data. */
  private columnRangeCache: Record<string, { min: number; max: number }> = {};
  private computeColumnRanges(): void {
    this.columnRangeCache = {};
    const props = Object.keys(this.visualRules);
    if (!props.length) return;
    props.forEach((prop) => {
      let min = Infinity, max = -Infinity;
      this.flattenedRows.forEach((r) => {
        if (r?.isTotal) return;
        const n = Number(r?.[prop]);
        if (Number.isFinite(n)) { if (n < min) min = n; if (n > max) max = n; }
      });
      if (min !== Infinity) this.columnRangeCache[prop] = { min, max };
    });
  }
  /** 0..1 position of a value within its column range (for bar width / heat). */
  private normValue(prop: string, value: any): number {
    const range = this.columnRangeCache[prop];
    const n = Number(value);
    if (!range || !Number.isFinite(n) || range.max === range.min) return 0;
    return Math.max(0, Math.min(1, (n - range.min) / (range.max - range.min)));
  }

  /** Which visual a column uses ('databar'|'progress'|'heatmap'|'conditional'|
   *  'kpi'|'badge'|'trend'|'sparkline'|'icon'), from config. */
  visualKind(prop: string): string { return String(this.visualRules[prop]?.kind || ''); }

  /** Inline background color for a heat-map / conditional cell (or ''). */
  cellBg(prop: string, value: any): string {
    const rule = this.visualRules[prop];
    if (!rule) return '';
    if (rule.kind === 'heatmap') {
      const t = this.normValue(prop, value);
      const from = rule.from || [239, 246, 255], to = rule.to || [37, 99, 235];
      const mix = (i: number) => Math.round(from[i] + (to[i] - from[i]) * t);
      return `rgba(${mix(0)}, ${mix(1)}, ${mix(2)}, ${rule.alpha ?? 0.85})`;
    }
    if (rule.kind === 'conditional') {
      const m = this.matchCondition(rule, Number(value));
      return m?.background || '';
    }
    return '';
  }
  cellFg(prop: string, value: any): string {
    const rule = this.visualRules[prop];
    if (rule?.kind === 'conditional') return this.matchCondition(rule, Number(value))?.color || '';
    return '';
  }
  /** Data-bar / progress width (%) for a cell. */
  barWidth(prop: string, value: any): number {
    const rule = this.visualRules[prop];
    if (rule?.kind === 'progress') { const n = Number(value); return Math.max(0, Math.min(100, n)); }
    return Math.round(this.normValue(prop, value) * 100);
  }
  barColor(prop: string): string { return String(this.visualRules[prop]?.color || '#3b82f6'); }

  /** First matching conditional rule for a value. */
  private matchCondition(rule: any, n: number): any {
    if (!Number.isFinite(n) || !Array.isArray(rule?.rules)) return null;
    for (const r of rule.rules) {
      const v = Number(r.value), v2 = Number(r.value2);
      const ok =
        (r.op === '>' && n > v) || (r.op === '>=' && n >= v) ||
        (r.op === '<' && n < v) || (r.op === '<=' && n <= v) ||
        (r.op === '=' && n === v) || (r.op === '!=' && n !== v) ||
        (r.op === 'between' && n >= v && n <= v2);
      if (ok) return r;
    }
    return null;
  }
  /** KPI / status-badge label + class for a cell. */
  badgeFor(prop: string, value: any): { text: string; cls: string } | null {
    const rule = this.visualRules[prop];
    if (rule?.kind !== 'kpi' && rule?.kind !== 'badge') return null;
    const m = this.matchCondition(rule, Number(value));
    return m ? { text: m.label || String(value), cls: m.badgeClass || 'tm-badge-neutral' } : { text: String(value ?? ''), cls: 'tm-badge-neutral' };
  }
  /** Trend arrow + class for a value (positive/negative/flat). */
  trendFor(value: any): { icon: string; cls: string } {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return { icon: '–', cls: 'tm-trend-flat' };
    return n > 0 ? { icon: '▲', cls: 'tm-trend-up' } : { icon: '▼', cls: 'tm-trend-down' };
  }
  /** Build an SVG polyline points string for a sparkline from an array cell. */
  sparkPoints(value: any): string {
    const arr = Array.isArray(value) ? value.map(Number).filter(Number.isFinite) : [];
    if (arr.length < 2) return '';
    const w = 60, h = 18, min = Math.min(...arr), max = Math.max(...arr), span = max - min || 1;
    return arr.map((v, i) => `${(i / (arr.length - 1)) * w},${h - ((v - min) / span) * h}`).join(' ');
  }
  sparkBars(value: any): Array<{ x: number; h: number; w: number }> {
    const arr = Array.isArray(value) ? value.map(Number).filter(Number.isFinite) : [];
    if (!arr.length) return [];
    const w = 60, h = 18, max = Math.max(...arr, 1), bw = w / arr.length;
    return arr.map((v, i) => ({ x: i * bw, h: Math.max(1, (v / max) * h), w: Math.max(1, bw - 1) }));
  }

  // ── Editable measure columns (config-driven, mirrors the Normal table's
  // per-column cell-editing) ──────────────────────────────────────────────────
  // A measure column is editable when its header carries `editable: true` (set
  // by the builder from the measure's own Editable flag) or its prop is listed
  // in `data.editableColumns`. Only LEAF rows get an input — a parent row is an
  // aggregate, so editing it is ambiguous; instead parents + the grand total
  // re-sum from their children after each edit (recalcTotalsOnEdit, default on).
  /** Emitted on "Save changes" with every pending cell edit. The HOST owns
   *  persistence (same contract as the editable table's no-URL save path);
   *  local change state settles optimistically on emit. */
  @Output() save = new EventEmitter<DrillDownSaveEvent>();

  /** Nodes with pending edits → the set of edited measure props on each. Keys
   *  are the CLONED tree nodes (cloneRows), which stay stable across
   *  expand/collapse/sort — only a full data reload (renderResult) resets them. */
  private editedCells = new Map<any, Set<string>>();

  private get editablePropsSet(): Set<string> {
    const props = new Set<string>();
    ((this.data?.headers as any[]) || []).forEach((h) => { if (h?.editable === true && h.prop) props.add(String(h.prop)); });
    const listed = this.data?.editableColumns;
    if (Array.isArray(listed)) listed.forEach((p: unknown) => props.add(String(p)));
    return props;
  }
  get editingEnabled(): boolean { return this.editablePropsSet.size > 0; }
  get recalcTotalsOnEdit(): boolean { return this.data?.recalcTotalsOnEdit !== false; }
  get hasEdits(): boolean { return this.editedCells.size > 0; }
  get editedCount(): number {
    let n = 0;
    this.editedCells.forEach((props) => { n += props.size; });
    return n;
  }
  isCellEdited(row: any, prop: string): boolean { return this.editedCells.get(row)?.has(prop) ?? false; }
  /** Which rows a cell of `prop` may be edited on. Total / grand-total rows
   *  never edit. Otherwise, in order:
   *  1. the header's `editableLevels: [1, 2, …]` (builder: the measure's
   *     "Editable on levels" pick) → exactly those 1-based levels, so a table
   *     can offer the dropdown on level 1 only, level 2 only, or any mix;
   *  2. no pick + TEXT column (header type 'text', e.g. a "Planning Status"
   *     dropdown) → every level, top-level parents included (no roll-up);
   *  3. no pick + NUMBER column → leaf rows only (parents are re-summed
   *     aggregates). A parent number edited via (1) is pinned — see
   *     recalcAncestors — so a later child edit doesn't overwrite it. */
  isRowEditable(row: any, prop?: string): boolean {
    if (row?.isTotal || row?.isGrandTotal) return false;
    const header = prop ? this.headerFor(prop) : null;
    const levels = header?.editableLevels;
    if (Array.isArray(levels) && levels.length) return levels.map(Number).includes(Number(row?.level));
    if (header?.type === 'text') return true;
    return !(row?.children?.length);
  }

  // ── Click-to-edit ────────────────────────────────────────────────────────
  // An editable cell shows its backend VALUE by default (plain text), same as
  // a read-only cell — the input/select only appears once the user clicks it,
  // so a row full of already-answered dropdowns doesn't read as one big open
  // form. Only one cell is "active" (showing its control) at a time.
  private activeEditCell: { row: any; prop: string } | null = null;
  isActiveEditCell(row: any, prop: string): boolean {
    return this.activeEditCell?.row === row && this.activeEditCell?.prop === prop;
  }
  beginCellEdit(row: any, prop: string, event?: Event): void {
    event?.stopPropagation();
    if (!this.isRowEditable(row, prop)) return;
    this.activeEditCell = { row, prop };
  }
  /** Close the active editor back to plain-text display — after a commit, or
   *  the control losing focus without one (blur / click elsewhere). */
  closeCellEdit(): void {
    this.activeEditCell = null;
  }

  // ── Editable tree-column LABELS (per-level rename) ─────────────────────────
  // A level flagged Editable in the builder lands on the tree header as
  // `editableLevels: [1, 2, …]`; rows of those levels render their name as a
  // text input. A label edit is a RENAME — no totals to recompute — and rides
  // the same dirty state / Save / Discard as measure edits.
  private get editableLabelLevels(): Set<number> {
    const header = ((this.data?.headers as any[]) || []).find((h) => h?.expandabile);
    const levels = header?.editableLevels ?? this.data?.editableLevels;
    return new Set(Array.isArray(levels) ? levels.map(Number) : []);
  }
  isLabelEditable(row: any): boolean {
    return !row?.isTotal && this.editableLabelLevels.has(Number(row?.level));
  }
  /** The dataset field behind a level's labels (builder passes `levelFields`:
   *  level → dimension field); falls back to the display prop. */
  private levelField(level: number): string {
    const map = (this.data?.levelFields as Record<string | number, string>) || {};
    return String(map[level] ?? map[String(level)] ?? this.nameProp);
  }
  /** Commit a renamed node label (on change/blur). Empty input = no-op (the
   *  refresh snaps the input back to the current name). */
  /** `finalize=false` (the `input` event, i.e. every keystroke) only updates the
   *  dirty state — enough for the Save bar to appear immediately — without the
   *  re-flatten below, which would yank focus mid-typing. `finalize=true` (the
   *  `change` event, on blur/Enter) does that full refresh once typing stops. */
  onLabelCommit(row: any, rawValue: string, finalize = true): void {
    const prop = this.nameProp;
    const value = String(rawValue ?? '').trim();
    if (!value || value === String(row[prop] ?? '')) { if (finalize) this.afterEditRefresh(); return; }
    if (!row.__orig) row.__orig = {};
    if (!(prop in row.__orig)) row.__orig[prop] = row[prop];
    row[prop] = value;
    let props = this.editedCells.get(row);
    if (!props) { props = new Set<string>(); this.editedCells.set(row, props); }
    props.add(prop);
    // Typing the original name back clears the pending edit for that cell.
    if (String(row.__orig[prop] ?? '') === value) {
      props.delete(prop);
      delete row.__orig[prop];
      if (!props.size) this.editedCells.delete(row);
    }
    if (finalize) this.afterEditRefresh();
  }

  /** Commit an input's value into the node (on change/blur — not per keystroke,
   *  so the re-flatten below never yanks focus mid-typing). */
  /** Header lookup by prop (the builder marks text attribute columns `type: 'text'`). */
  private headerFor(prop: string): any {
    return ((this.data?.headers as any[]) || []).find((h) => h?.prop === prop);
  }
  isTextProp(prop: string): boolean { return this.headerFor(prop)?.type === 'text'; }

  // ── Dropdown editors (custom values and/or the values already in the table) ─
  // A measure header carrying `editor: 'dropdown'`, or the tree header's
  // `levelEditors[level]` for a label rename, renders a <select> instead of
  // the input. Its options are the configured CUSTOM values (`options` — they
  // need not exist in the data at all) ∪, when `optionsFromData` / `fromData`,
  // the distinct values the column / level currently holds ∪ the cell's own
  // current value (so a value outside the list still shows and stays
  // re-selectable). Commits go through the same onCellValueCommit /
  // onLabelCommit paths as the inputs, so dirty state, re-sum and Save are
  // untouched.
  /** Distinct data values per measure prop (and per `level:<n>` for label
   *  levels), recomputed on every data load / edit refresh. */
  private dropdownDataValues: Record<string, string[]> = {};
  isDropdownProp(prop: string): boolean { return this.headerFor(prop)?.editor === 'dropdown'; }
  private levelEditor(level: number): { options?: unknown; fromData?: boolean } | null {
    const header = ((this.data?.headers as any[]) || []).find((h) => h?.expandabile);
    const editors = header?.levelEditors ?? this.data?.levelEditors;
    const spec = editors?.[level] ?? editors?.[String(level)];
    return spec && typeof spec === 'object' ? spec : null;
  }
  isLabelDropdown(row: any): boolean {
    return this.isLabelEditable(row) && !!this.levelEditor(Number(row?.level));
  }
  private computeDropdownValues(): void {
    this.dropdownDataValues = {};
    const props = ((this.data?.headers as any[]) || [])
      .filter((h) => !h?.expandabile && h?.editor === 'dropdown' && h?.optionsFromData === true)
      .map((h) => String(h.prop));
    const levels = new Set<number>();
    this.editableLabelLevels.forEach((level) => { if (this.levelEditor(level)?.fromData) levels.add(level); });
    if (!props.length && !levels.size) return;
    const sets: Record<string, Set<string>> = {};
    const add = (key: string, value: unknown): void => {
      if (value === null || value === undefined) return;
      const text = String(value).trim();
      if (!text) return;
      if (!sets[key]) sets[key] = new Set<string>();
      sets[key].add(text);
    };
    const walk = (nodes: any[]): void => (nodes || []).forEach((node) => {
      if (!node || node.isGrandTotal || node.isTotal) return;
      props.forEach((prop) => add(prop, node[prop] ?? node.measures?.[prop]));
      const level = Number(node.level);
      if (levels.has(level)) add(`level:${level}`, node[this.nameProp]);
      if (Array.isArray(node.children)) walk(node.children);
    });
    walk(this.rawData || []);
    Object.keys(sets).forEach((key) => {
      this.dropdownDataValues[key] = Array.from(sets[key]).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    });
  }
  private mergeOptions(custom: unknown, dataKey: string | null, current: unknown): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    const push = (value: unknown): void => {
      const text = String(value ?? '').trim();
      if (text && !seen.has(text)) { seen.add(text); out.push(text); }
    };
    (Array.isArray(custom) ? custom : []).forEach(push);
    if (dataKey) (this.dropdownDataValues[dataKey] || []).forEach(push);
    push(current);
    return out;
  }
  /** Options for a measure cell's select (custom ∪ data ∪ current value). */
  cellOptions(row: any, prop: string): string[] {
    const header = this.headerFor(prop);
    return this.mergeOptions(header?.options, header?.optionsFromData === true ? prop : null, row?.[prop]);
  }
  /** Options for a label cell's select. */
  labelOptions(row: any): string[] {
    const level = Number(row?.level);
    const spec = this.levelEditor(level);
    return this.mergeOptions(spec?.options, spec?.fromData ? `level:${level}` : null, row?.[this.nameProp]);
  }
  /** Whether a select option matches the cell's current value (number cells
   *  hold numbers; options are strings). */
  isOptionSelected(option: string, value: unknown): boolean {
    return value !== null && value !== undefined && String(value).trim() === option;
  }

  /** Free-text attribute edit (e.g. "Voice of Business"): stored as a string,
   *  no ancestor / total re-sum. Same dirty state + Save / Discard as numbers. */
  private onTextValueCommit(row: any, prop: string, rawValue: string, finalize = true): void {
    const value = String(rawValue ?? '').trim();
    const current = String(row[prop] ?? '').trim();
    if (value === current) return;
    if (!row.__orig) row.__orig = {};
    if (!(prop in row.__orig)) row.__orig[prop] = row[prop];
    row[prop] = value === '' ? null : value;
    if (row.measures && typeof row.measures === 'object') row.measures[prop] = row[prop];
    let props = this.editedCells.get(row);
    if (!props) { props = new Set<string>(); this.editedCells.set(row, props); }
    props.add(prop);
    if (String(row.__orig[prop] ?? '').trim() === value) {
      props.delete(prop);
      delete row.__orig[prop];
      if (!props.size) this.editedCells.delete(row);
    }
    if (finalize) this.afterEditRefresh();
  }

  /** `finalize=false` (the `input` event, i.e. every keystroke) only updates the
   *  dirty state — enough for the Save bar to appear immediately — without the
   *  re-sum/re-flatten below, which would yank focus mid-typing. `finalize=true`
   *  (the `change` event, on blur/Enter) does that full refresh once typing stops. */
  onCellValueCommit(row: any, prop: string, rawValue: string, finalize = true): void {
    if (this.isTextProp(prop)) { this.onTextValueCommit(row, prop, rawValue, finalize); return; }
    const parsed = String(rawValue ?? '').trim() === '' ? null : Number(rawValue);
    // A NON-numeric pick on a Number column (a dropdown of statuses on a
    // measure left as "Number (aggregated)") used to be coerced to null and
    // silently dropped — no dirty state, no Save bar. Keep it as text instead
    // (re-sum skips non-numeric cells; Save reports it with kind 'text').
    if (parsed !== null && !Number.isFinite(parsed)) { this.onTextValueCommit(row, prop, rawValue, finalize); return; }
    const value = parsed !== null && Number.isFinite(parsed) ? parsed : null;
    const current = row[prop];
    if (value === null && (current === null || current === undefined || current === '')) return;
    if (value !== null && Number(current) === value) return;
    if (!row.__orig) row.__orig = {};
    if (!(prop in row.__orig)) row.__orig[prop] = current;
    row[prop] = value;
    if (row.measures && typeof row.measures === 'object') row.measures[prop] = value;
    let props = this.editedCells.get(row);
    if (!props) { props = new Set<string>(); this.editedCells.set(row, props); }
    props.add(prop);
    // Typing the original value back clears the pending edit for that cell.
    if (String(row.__orig[prop] ?? '') === String(value ?? '')) {
      props.delete(prop);
      delete row.__orig[prop];
      if (!props.size) this.editedCells.delete(row);
    }
    if (!finalize) return;
    if (this.recalcTotalsOnEdit) this.recalcAncestors(row, prop);
    this.afterEditRefresh();
  }

  /** Re-sum this measure up the parent chain + the sticky grand total. Uses SUM
   *  regardless of the measure's configured roll-up (AVG/MIN/MAX aggregates
   *  can't be reproduced client-side without the raw records) — turn
   *  recalcTotalsOnEdit off for those tables. */
  private recalcAncestors(row: any, prop: string): void {
    if (this.isTextProp(prop)) return; // attributes are never summed
    let parent = row.parent;
    while (parent) {
      // A parent the user edited directly (editableLevels lets non-leaf rows
      // edit) keeps its manual value while that edit is pending — it is NOT
      // re-summed from its children, though its own ancestors still sum it.
      if (!this.editedCells.get(parent)?.has(prop)) {
        const sum = (parent.children || []).reduce((acc: number, child: any) => {
          const n = Number(child?.[prop]);
          return Number.isFinite(n) ? acc + n : acc;
        }, 0);
        parent[prop] = sum;
        if (parent.measures && typeof parent.measures === 'object') parent.measures[prop] = sum;
      }
      parent = parent.parent;
    }
    this.recalcGrandTotal(prop);
  }

  private recalcGrandTotal(prop: string): void {
    // Only measures the backend included in the grand total participate — a
    // null/absent cell there means "not totalled", and stays that way.
    if (!this.grandTotalRow || this.grandTotalRow[prop] === null || this.grandTotalRow[prop] === undefined) return;
    const sum = ((this.rawData as any[]) || []).reduce((acc: number, node: any) => {
      const n = Number(node?.[prop]);
      return Number.isFinite(n) ? acc + n : acc;
    }, 0);
    const header = ((this.data?.headers as any[]) || []).find((h) => h?.prop === prop);
    // Match setTotal's formatting so the sticky row renders consistently.
    const decimals = typeof header?.decimals === 'number' ? Math.max(0, Math.min(6, Math.floor(header.decimals))) : this.numberDecimals;
    this.grandTotalRow[prop] = header?.type === 'percentage'
      ? (header?.percentScale === 'fraction' ? sum * 100 : sum).toFixed(decimals)
      : sum.toFixed(decimals);
  }

  // ── Formula (computed) columns — merged Drill-Down + Pivot ─────────────────
  // `data.drillFormulas` carries the formula definitions + the base measure keys
  // they read (and the pivot column values when measures are fanned out). The
  // host evaluated them when it built the tree; after a cell edit re-sums the
  // parents, every formula is re-evaluated here so a ratio like Margin % stays
  // correct at every level and in the grand total.
  private readonly formulaService = inject(SelfServiceFormulaService);
  private get drillFormulas(): DrillFormulaMeta | null {
    const meta = this.data?.drillFormulas as DrillFormulaMeta | undefined;
    return meta && Array.isArray(meta.computedColumns) && meta.computedColumns.length ? meta : null;
  }
  private reapplyFormulas(): void {
    const meta = this.drillFormulas;
    if (!meta) return;
    applyDrillFormulasToTree(
      this.rawData as Array<Record<string, unknown>>,
      (this.grandTotalRow as Record<string, unknown> | undefined) ?? null,
      meta,
      (expression, names) => this.formulaService.buildRowEvaluator(expression, names),
    );
  }

  /** Rebuild the derived render state after an edit/discard: re-flattening
   *  regenerates the embedded per-level Total rows from the (re-summed) parent
   *  nodes; visual-rule ranges rescale to the new values. */
  private afterEditRefresh(): void {
    this.reapplyFormulas();
    this.flattenTree(this.rawData);
    this.computeColumnRanges();
    this.computeDropdownValues();
    this.updateHighlightedCells();
  }

  /** Root→node display path (for the save payload's record address). */
  private nodePath(row: any): string[] {
    const parts: string[] = [];
    let node = row;
    let guard = 0;
    while (node && guard++ < 32) {
      parts.unshift(String(node[this.nameProp] ?? ''));
      node = node.parent;
    }
    return parts;
  }

  saveEdits(): void {
    if (!this.hasEdits) return;
    const changes: DrillDownCellChange[] = [];
    this.editedCells.forEach((props, node) => {
      props.forEach((prop) => {
        const isLabel = prop === this.nameProp;
        const raw = node[prop];
        // Text column, or a non-numeric pick kept as text on a Number column.
        const isText = !isLabel && (this.isTextProp(prop) || (typeof raw === 'string' && raw.trim() !== '' && !Number.isFinite(Number(raw))));
        const rowValues: Record<string, unknown> = { ...(node.measures && typeof node.measures === 'object' ? node.measures : {}) };
        Object.entries(node).forEach(([key, value]) => {
          if (['parent', 'children', '__orig', 'measures', 'expanded', 'level', 'isTotal', 'isGrandTotal'].includes(key)) return;
          if (value !== null && value !== undefined && typeof value === 'object') return;
          // A null/undefined top-level duplicate must never clobber a real value
          // the measures bag already has for the same key — hidden business keys
          // such as `uid` live there and must win over an absent/null echo.
          if ((value === null || value === undefined) && rowValues[key] !== null && rowValues[key] !== undefined) return;
          rowValues[key] = value;
        });
        changes.push({
          path: this.nodePath(node),
          name: String(node[this.nameProp] ?? ''),
          level: Number(node.level ?? 0),
          // Label edit → report the LEVEL's dataset field, not the display prop.
          field: isLabel ? this.levelField(Number(node.level ?? 0)) : prop,
          value: raw === null || raw === undefined ? null : (isLabel || isText ? String(raw) : Number(raw)),
          oldValue: node.__orig?.[prop],
          rowValues,
          ...(isText ? { kind: 'text' as const } : {}),
        });
      });
    });
    this.save.emit({ widgetId: this.widgetId, changes });
    // Optimistic settle — the host owns persistence (toast / endpoint POST),
    // mirroring the editable table's no-URL save; the edited values become the
    // new local baseline so Save/Discard hide.
    this.editedCells.forEach((_props, node) => { delete node.__orig; });
    this.editedCells.clear();
  }

  discardEdits(): void {
    if (!this.hasEdits) return;
    const entries: Array<{ node: any; prop: string }> = [];
    this.editedCells.forEach((props, node) => props.forEach((prop) => entries.push({ node, prop })));
    entries.forEach(({ node, prop }) => {
      node[prop] = node.__orig?.[prop];
      // Mirror into `measures` only for measure props — the label lives on the
      // node itself, not in the measures bag.
      if (prop !== this.nameProp && node.measures && typeof node.measures === 'object') node.measures[prop] = node[prop];
    });
    this.editedCells.forEach((_props, node) => { delete node.__orig; });
    this.editedCells.clear();
    // Re-sum only MEASURE props — a restored label has no totals to rebuild
    // (and summing name strings would write NaN into every ancestor).
    if (this.recalcTotalsOnEdit) entries.forEach(({ node, prop }) => { if (prop !== this.nameProp) this.recalcAncestors(node, prop); });
    this.afterEditRefresh();
  }

  flattenedRows: any[] = [];
  visibleColumns: any[] = [];
  totalColumnWidth = 0;
  rawData: any;
  showTotalLevels: number[] = [];
  protected filterSub!: Subscription;

  /** Exact rendered pixel width of each header column, measured straight from
   *  the DOM (see measureColumnWidths). The sticky total row copies these
   *  directly instead of trying to re-derive ngx-datatable's own column-width
   *  algorithm (which changes under columnMode 'force' vs 'standard', on
   *  resize, per column-count, etc.) - copying the real measured value is the
   *  only way to guarantee the total row's dividers land exactly on the real
   *  column boundaries, in every mode, every time. */
  columnWidths: number[] = [];
  private resizeObserver?: ResizeObserver;
  private handledScrollbarOffset = 0;
  private mirroredScrollbarOffset: number | null = null;

  @ViewChild('treeCell', { static: true }) treeCell!: TemplateRef<any>;
  @ViewChild('percentageCell', { static: true })
  percentageCell!: TemplateRef<any>;
  @ViewChild('numberCell', { static: true }) numberCell!: TemplateRef<any>;
  @ViewChild('editCell', { static: true }) editCell!: TemplateRef<any>;
  @ViewChild('visualCell', { static: true }) visualCell!: TemplateRef<any>;
  @ViewChild('infoIconHeader', { static: true }) infoIconHeader!: TemplateRef<any>;

  selectedCell: any;
  isLoading = true;
  highlightedCells: any = [];
  customerSpec: any = [];
  customerNameKeys: Set<string> = new Set();
  engineModelKeys: Set<string> = new Set();
  customerSpecKeys: Set<string> = new Set();
  protected unSubscribe$: Subject<void> = new Subject();
  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
  ) {}

  @ViewChild('table', { static: true }) table: any;
  @ViewChild('totalRow', { static: true }) totalRow!: ElementRef<HTMLDivElement>;
  @ViewChild('horizontalScrollbar', { static: true }) horizontalScrollbar!: ElementRef<HTMLDivElement>;
  /** Two-tier header band for pivoted measures ("Quantity" over 2024|2025|2026).
   *  Rendered only when at least one visible column carries a `group`. */
  @ViewChild('groupBand') groupBand?: ElementRef<HTMLDivElement>;
  groupBands: Array<{ label: string; span: number; start: number }> = [];
  hasHeaderGroups = false;
  /** ngx-datatable header row height. 40px for single-line labels; grows to
   *  two lines when a visible column's label is too long for its width
   *  ("Price Change Percentage" in a 130px pivot column) — the label wraps
   *  (see the header-cell span CSS) instead of being clipped. */
  headerHeight = 40;
  private static readonly HEADER_LINE_HEIGHT = 40;
  private static readonly HEADER_TWO_LINE_HEIGHT = 58;
  /** Whether `name` needs a second line inside a column of `width` px:
   *  a conservative per-character estimate against the cell's text area
   *  (padding 16px each side + the sort button). */
  private headerLabelWraps(name: string, width: number): boolean {
    const scale = Number(getComputedStyle(document.documentElement).getPropertyValue('--dvm-font-scale')) || 1;
    const textWidth = String(name || '').length * 7.2 * scale;
    const available = Math.max(0, (width || 130) - 32 - 22);
    return textWidth > available;
  }
  /** Data loading runs here, NOT in ngAfterViewInit: `renderResult` fills
   *  `flattenedRows`, and filling it after the first check is NG0100
   *  ("Previous value: '[]'"). ngAfterViewInit keeps only view measurement. */
  ngOnInit(): void {
    // Pre-fetched data path: when the host has ALREADY called the backend and
    // just wants this component to render the response (e.g. the DVM Layout
    // Builder / published dashboard), render it directly and do not subscribe
    // to global filters. Otherwise a non-target DVM input change can make this
    // table self-reload and blank itself outside the published target filter.
    if (this.data?.staticData) {
      this.renderResult(this.data.staticData);
      return;
    }
    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          this.filter = filters;

          // reset the drilldown on reset button click
          if (!filters || Object.keys(filters).length === 0) {
            if (!this.data.filterListner || this.data.filterListner.length === 0) {
              this.isLoading = true;
              return this.loadAndRenderChart(filters);
            }
            //  Reset handling
            this.customerNameKeys.clear();
            this.engineModelKeys.clear();
            this.customerSpecKeys.clear();
            this.highlightedCells = [];
            this.customerSpec = [];

            if (this.rawData) {
              this.rawData.forEach((node: any) => (node.expanded = false));
              this.flattenTree(this.rawData);
              this.updateVisibleColumns();
            }

            return EMPTY;
          }

          if (filters?.widgetId === this.widgetId) {
            return EMPTY;
          }
          this.isLoading = true;
          return this.loadAndRenderChart(filters);
        }),
      )

      .subscribe({
        next: (res: any) => this.renderResult(res),
        error: () => {
          this.isLoading = false;
        },
      });
  }

  ngAfterViewInit(): void {
    this.table.recalculate();
    this.refreshColumnWidths();

    // Re-measure whenever the table's own host element resizes (panel resize,
    // sidebar toggle, browser resize, etc.) - this is what actually keeps the
    // total row's column widths correct on a "responsive" layout, rather than
    // a one-shot delayed guess.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.refreshColumnWidths());
      this.resizeObserver.observe(this.table.element);
    }

    setTimeout(() => {
      // Re-measure now that the surrounding layout (card/panel sizing, our
      // now-content-sized wrapper, etc.) has actually settled - calling
      // recalculate() only once, synchronously in ngAfterViewInit, can catch
      // the host at a stale/narrower width and never re-stretch columns
      // under columnMode: 'force', leaving an unnecessary horizontal
      // scrollbar for columns that would otherwise fit.
      this.refreshColumnWidths();
    }, 500);
  }

  /** Horizontal scroll sync: ngx-datatable's `(scroll)` output carries the
   *  body's offsetX — the SAME value it shifts its own header by — so the group
   *  band above and the sticky Total row below follow the columns exactly.
   *  (A one-shot DOM listener on `.datatable-body` used to do this; it was
   *  attached 500ms after init and never re-attached, so a body created or
   *  replaced later — e.g. after a data swap — left the band and the Total row
   *  standing still while the columns scrolled.) */
  onTableScroll(event: { offsetX?: number; offsetY?: number } | null | undefined): void {
    const offsetX = Number(event?.offsetX ?? 0);
    if (!Number.isFinite(offsetX)) return;
    const body = (this.table?.element as HTMLElement | undefined)?.querySelector<HTMLElement>('.datatable-body');
    const scrollbar = this.horizontalScrollbar?.nativeElement;
    // ngx-datatable emits on an animation frame. A newer native movement may
    // already be waiting for its scroll event; never rewind that movement.
    if (body && Math.abs(body.scrollLeft - offsetX) >= 1) return;
    if (scrollbar && Math.abs(scrollbar.scrollLeft - this.handledScrollbarOffset) >= 1) return;
    this.syncHorizontalCompanions(offsetX, true);
  }

  /** The bottom scrollbar drives only the native datatable body. ngx-datatable
   *  observes that native scroll itself and emits onTableScroll on its normal
   *  animation frame. Avoiding a second manual call into the library prevents
   *  the header/body jump that occurred while dragging the thumb. */
  onHorizontalScrollbarScroll(event: Event): void {
    const offsetX = (event.currentTarget as HTMLElement | null)?.scrollLeft ?? 0;
    this.handledScrollbarOffset = offsetX;
    const mirrored = this.mirroredScrollbarOffset;
    this.mirroredScrollbarOffset = null;
    // Ignore the native event generated by our own table-to-track write.
    if (mirrored !== null && Math.abs(offsetX - mirrored) < 1) return;
    const tableHost = this.table?.element as HTMLElement | undefined;
    const body = tableHost?.querySelector<HTMLElement>('.datatable-body');
    if (!body || Math.abs(body.scrollLeft - offsetX) < 1) return;

    body.scrollLeft = offsetX;
    // Keep the external rows visually attached during the table library's
    // requestAnimationFrame; its emitted event will confirm the same position.
    this.syncHorizontalCompanions(body.scrollLeft, false);
  }

  private syncHorizontalCompanions(offsetX: number, syncScrollbar: boolean): void {
    if (this.totalRow) this.totalRow.nativeElement.scrollLeft = offsetX;
    if (this.groupBand) this.groupBand.nativeElement.scrollLeft = offsetX;

    if (!syncScrollbar) return;
    const scrollbar = this.horizontalScrollbar?.nativeElement;
    if (scrollbar && Math.abs(scrollbar.scrollLeft - offsetX) >= 1) {
      scrollbar.scrollLeft = offsetX;
      this.handledScrollbarOffset = scrollbar.scrollLeft;
      this.mirroredScrollbarOffset = scrollbar.scrollLeft;
    }
  }

  /** Re-render when the host swaps in new pre-fetched data (e.g. the DVM Layout
   *  Builder updating a tile after an input-control recompute). Without this the
   *  rows are only built once in the initial render, so a data refresh updated
   *  the headers but left the table showing "No data to display". First change
   *  is handled by the initial render; skip it here. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.data?.staticData) {
      this.renderResult(this.data.staticData);
    }
  }

  /** Reshape + render a getConfig response. Accepts either casing — some
   *  backends respond with `Data`/`Total`, others (e.g. the DVM Layout
   *  Builder's analytics engine) with lowercase `data`/`total`. Also accepts
   *  the kaveri-lite drill-down engine's shape, where the grand total isn't a
   *  separate key at all - it's an extra node embedded at the end of `data`
   *  (`isGrandTotal: true`, `level: 0`, `measures: {...}`), which we pull out
   *  here so it never renders as a normal tree row. */
  protected renderResult(res: any): void {
    const rawRows: any[] = res?.Data ?? res?.data ?? [];
    // Clone before flattenTree mutates nodes with a `parent` back-reference —
    // without this, that circular reference lands on the SAME objects the host
    // widget config holds (e.g. the published dashboard's `pageContent`), which
    // later breaks `JSON.stringify` (used by HttpClient to serialize the body)
    // with "Converting circular structure to JSON" when that config is saved/published.
    const rows: any[] = Array.isArray(rawRows) ? this.cloneRows(rawRows) : rawRows;
    const embeddedGrandTotal = Array.isArray(rows) ? rows.find((node: any) => node?.isGrandTotal) : undefined;
    this.rawData = Array.isArray(rows) ? rows.filter((node: any) => !node?.isGrandTotal) : rows;
    this.applyTopLevelGroupOrder(this.rawData);

    // Fresh clones → any pending cell edits refer to dead objects; drop them.
    this.editedCells.clear();

    const total = res?.Total ?? res?.total ?? embeddedGrandTotal?.measures;
    this.showTotalLevels = this.getShowTotalLevels();
    this.grandTotalRow = total ? this.setTotal(total, 0) : undefined;
    if (this.enableSavedViews) this.restoreView();
    this.flattenTree(this.rawData);
    this.computeColumnRanges();
    this.computeDropdownValues();
    this.updateVisibleColumns();
    this.isLoading = false;
    this.refreshColumnWidths();
  }

  /** Fixed top-level display order for the "Options Breakdown" widget's
   *  "Type Grouping" rows - Base Options first, then Additional Options,
   *  then Routing Cost - matching the same order the "valueOrder" dimension
   *  config already declares for the backend. Applied client-side (stable
   *  sort) so the UI shows this order regardless of what the backend
   *  actually returns. Any group name not in this list keeps its existing
   *  relative position after the ones that are, so this is a no-op for
   *  every OTHER drill-down table's groups. */
  private static readonly TOP_LEVEL_GROUP_ORDER = ['base options', 'additional options', 'routing cost'];
  private applyTopLevelGroupOrder(rows: any[]): void {
    if (!Array.isArray(rows) || rows.length < 2) return;
    const rank = (row: any): number => {
      const name = String(row?.[this.nameProp] ?? '').trim().toLowerCase();
      const index = DrillDownComponent.TOP_LEVEL_GROUP_ORDER.indexOf(name);
      return index === -1 ? DrillDownComponent.TOP_LEVEL_GROUP_ORDER.length : index;
    };
    rows.sort((a, b) => rank(a) - rank(b));
  }

  protected loadAndRenderChart(filters?: any): any {
    this.highlightedCells = [];
    this.customerSpec = [];
    this.customerNameKeys.clear();
    this.engineModelKeys.clear();
    this.customerSpecKeys.clear();
    return this.configService
      .getConfig(this.data.apiUrl, this.data, {
        ...this.filter,
        useQueryParams: this.data?.useQueryParams || false,
      })
      .pipe(takeUntil(this.unSubscribe$));
  }
  toggleExpand(row: any): void {
    row.expanded = !row.expanded;
    this.flattenTree(this.rawData);
    this.updateVisibleColumns();
    this.updateHighlightedCells();
    this.refreshColumnWidths();
  }

  /** Expand or collapse EVERY node in the tree (Expand/Collapse All). */
  setAllExpanded(expanded: boolean): void {
    const walk = (nodes: any[]): void => {
      (nodes || []).forEach((n) => { if (n.children?.length) { n.expanded = expanded; walk(n.children); } });
    };
    walk(this.rawData || []);
    this.flattenTree(this.rawData);
    this.updateVisibleColumns();
    this.refreshColumnWidths();
  }

  // ── Export / print (client-side) ────────────────────────────────────────────
  /** Flatten the currently-visible rows into a [headers, ...rows] matrix. */
  private exportMatrix(): { header: string[]; rows: string[][] } {
    const cols = this.visibleColumns;
    // Pivoted columns export under their full "Quantity 2024"-style label.
    const header = cols.map((c) => (c.group ? `${c.group} ${c.name}` : c.name));
    const rows = this.flattenedRows.map((r) =>
      cols.map((c) => {
        const v = r[c.prop];
        // Tree column: indent by level so the hierarchy survives the export.
        if (c.levelIndex !== undefined || c.indentByLevel) return `${'  '.repeat(Math.max(0, (r.level || 1) - 1))}${r[this.nameProp] ?? ''}`;
        return v === null || v === undefined ? '' : String(v);
      }),
    );
    return { header, rows };
  }
  exportCsv(): void {
    const { header, rows } = this.exportMatrix();
    const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
    const csv = [header.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${this.titleText}.csv`; a.click();
    URL.revokeObjectURL(url);
  }
  async exportExcel(): Promise<void> {
    const { header, rows } = this.exportMatrix();
    try {
      const XLSX: any = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Drill-Down');
      XLSX.writeFile(wb, `${this.titleText}.xlsx`);
    } catch { this.exportCsv(); }
  }
  /** PDF via the browser's print-to-PDF on a clean printable window. */
  exportPdf(): void { this.printTable(true); }
  printTable(_pdf = false): void {
    const { header, rows } = this.exportMatrix();
    const win = window.open('', '_blank');
    if (!win) return;
    const thead = `<tr>${header.map((h) => `<th>${h}</th>`).join('')}</tr>`;
    const tbody = rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('');
    win.document.write(`<html><head><title>${this.titleText}</title><style>
      body{font:12px system-ui;padding:16px} table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #cbd5e1;padding:4px 8px;text-align:left} th{background:#f1f5f9}
    </style></head><body><h3>${this.titleText}</h3><table>${thead}${tbody}</table></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  }

  // ── Saved views (localStorage: expand state + sort) ─────────────────────────
  private savedViewKey(): string { return `dvm.dd.view.${this.widgetId || this.titleText}`; }
  saveView(): void {
    try {
      const expandedPaths: string[] = [];
      const walk = (nodes: any[], path: string): void => (nodes || []).forEach((n) => {
        const p = `${path}/${n[this.nameProp] ?? ''}`;
        if (n.expanded) expandedPaths.push(p);
        if (n.children) walk(n.children, p);
      });
      walk(this.rawData || [], '');
      localStorage.setItem(this.savedViewKey(), JSON.stringify({ expandedPaths }));
    } catch { /* storage disabled */ }
  }
  private restoreView(): void {
    try {
      const raw = localStorage.getItem(this.savedViewKey());
      if (!raw) return;
      const { expandedPaths } = JSON.parse(raw) as { expandedPaths: string[] };
      const set = new Set(expandedPaths);
      const walk = (nodes: any[], path: string): void => (nodes || []).forEach((n) => {
        const p = `${path}/${n[this.nameProp] ?? ''}`;
        if (set.has(p) && n.children?.length) n.expanded = true;
        if (n.children) walk(n.children, p);
      });
      walk(this.rawData || [], '');
    } catch { /* ignore */ }
  }

  /** Re-runs ngx-datatable's own layout pass, then (after Angular finishes
   *  applying whatever DOM changes that triggered, e.g. columns array
   *  changing) copies the REAL rendered header-cell widths into
   *  columnWidths - see the field's doc comment for why this is measured
   *  rather than computed/guessed. */
  private refreshColumnWidths(): void {
    this.table?.recalculate();
    setTimeout(() => this.measureColumnWidths());
  }

  /** ngx-datatable's own column-drag-resize event (bound via `(resize)` on
   *  the table) - the header/body cells resize live through its internal
   *  logic, but our separately-measured `columnWidths` (used to size the
   *  custom sticky Total row) only re-measures on a container-level resize,
   *  data reload, or row expand/collapse otherwise. Without this, dragging a
   *  column's own divider left the Total row's cell widths stale, so its
   *  divisions no longer lined up with the just-resized column. No
   *  `recalculate()` here (unlike refreshColumnWidths) - ngx-datatable has
   *  already applied the new widths itself; we only need to re-read them. */
  onColumnResize(): void {
    setTimeout(() => this.measureColumnWidths());
  }

  private measureColumnWidths(): void {
    const host: HTMLElement | undefined = this.table?.element;
    if (!host) return;
    const headerCells = host.querySelectorAll<HTMLElement>(
      '.datatable-header .datatable-header-cell',
    );
    if (!headerCells.length) return;
    this.columnWidths = Array.from(headerCells).map((cell) => cell.offsetWidth);
    const body = host.querySelector<HTMLElement>('.datatable-body');
    const scrollbar = this.horizontalScrollbar?.nativeElement;
    // Match scroll RANGES, including the body's vertical scrollbar and border.
    // Summing header widths alone gives the two tracks different end positions.
    this.totalColumnWidth = body && scrollbar
      ? scrollbar.clientWidth + Math.max(0, body.scrollWidth - body.clientWidth)
      : this.columnWidths.reduce((sum, width) => sum + width, 0);
  }

  /** Deep-clones incoming rows (nested `children` included) so `flattenTree`'s
   *  `node.parent = parent` mutation lands on OUR OWN copy, not the shared
   *  object the host widget config holds. Plain data at this point (no
   *  `parent` back-references yet), so a structured clone is always safe here. */
  private cloneRows(rows: any[]): any[] {
    if (typeof structuredClone === 'function') return structuredClone(rows);
    return JSON.parse(JSON.stringify(rows));
  }

  // ── Latest-N-years ROW filter for the tree (a hierarchy LEVEL whose node
  // labels are years, e.g. Vertical > Segment > 2026) ─────────────────────────
  /** A node's label parsed as a PURE year ("2026" → 2026), else null. */
  private nodeYear(node: any): number | null {
    const match = String(node?.[this.nameProp] ?? '').trim().match(/^(?:19|20)\d{2}$/);
    return match ? Number(match[0]) : null;
  }
  /** The [min, max] window for year-labelled nodes, or null when the filter is
   *  off / no node label is a year. Base 'data' = max year found in the tree;
   *  default = the current SYSTEM year. */
  private nodeYearWindow(data: any[]): { min: number; max: number } | null {
    const count = this.yearFilterCount;
    if (!count) return null;
    const years: number[] = [];
    const walk = (nodes: any[]): void => (nodes || []).forEach((n) => {
      const year = this.nodeYear(n);
      if (year !== null) years.push(year);
      if (Array.isArray(n?.children)) walk(n.children);
    });
    walk(data || []);
    if (!years.length) return null;
    const max = this.yearFilterBase === 'data' ? Math.max(...years) : new Date().getFullYear();
    return { min: max - count + 1, max };
  }

  flattenTree(data: any[]): void {
    const result: any[] = [];
    // Latest-N-years: a node whose LABEL is a year outside the window is
    // skipped along with its whole subtree (evaluated per render, so published
    // tiles roll into the new year automatically).
    const yearWindow = this.nodeYearWindow(data);
    this.rebuildLevel2Groups(data);
    const traverse = (nodes: any[], parent: any = null): void => {
      for (const node of nodes) {
        if (yearWindow) {
          const year = this.nodeYear(node);
          if (year !== null && (year < yearWindow.min || year > yearWindow.max)) continue;
        }
        node.parent = parent;
        result.push(node);

        if (node.expanded && node.children) {
          traverse(node.children, node);

          const level = node.children[0]?.level;
          if (this.showTotalLevels.includes(level)) {
            const totalRow = this.setTotal(node, level);
            result.push(totalRow);
          }
        }
      }
    };
    traverse(data);
    this.flattenedRows = result;
  }
  onSort(event: any): void {
    const sortProp = event.sorts[0].prop;
    const dir = event.sorts[0].dir; // 'asc' or 'desc'
    this.sortTree(this.rawData, sortProp, dir);
    this.flattenTree(this.rawData);
  }
  sortTree(data: any[], prop: string, dir: 'asc' | 'desc'): void {
    data.sort((a, b) => {
      const valA = a[prop];
      const valB = b[prop];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === 'string') {
        return dir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      return dir === 'asc' ? valA - valB : valB - valA;
    });

    for (const item of data) {
      if (item.children) {
        this.sortTree(item.children, prop, dir);
      }
    }
  }

  // ── Latest-N-years column filter (config-driven) ────────────────────────────
  // `data.yearFilterCount = N` keeps only the value columns whose name/prop
  // carries a year inside the latest-N window (e.g. N=3 → 2024/2025/2026);
  // columns with NO year token (e.g. "Voice of Business") always stay visible.
  // The window's top year comes from `data.yearFilterBase`:
  //   'data' (default) → the highest year present in the columns/tree — future
  //                      years count ("latest 3" of 2024–2028 = 2026/27/28);
  //   'current'        → today's SYSTEM year (opt-in to hide future years).
  // Evaluated on every render — a published dashboard rolls to the new year
  // by itself, no regeneration needed.
  get yearFilterCount(): number {
    const n = Number(this.data?.yearFilterCount);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0; // 0 = filter off
  }
  get yearFilterBase(): string { return String(this.data?.yearFilterBase || 'data'); }

  /** First 4-digit year token in a column's prop/name (e.g. "2026H1 Net Sales"
   *  → 2026), or null when the column isn't year-scoped. */
  private headerYear(header: any): number | null {
    const match = `${header?.prop ?? ''} ${header?.name ?? ''}`.match(/(?:19|20)\d{2}/);
    return match ? Number(match[0]) : null;
  }

  /** The inclusive [min, max] year window, or null when the filter is off /
   *  no column carries a year at all. */
  private yearWindow(): { min: number; max: number } | null {
    const count = this.yearFilterCount;
    if (!count) return null;
    const years = ((this.data?.headers as any[]) || [])
      .filter((h) => !h?.expandabile)
      .map((h) => this.headerYear(h))
      .filter((y): y is number => y !== null);
    if (!years.length) return null;
    const max = this.yearFilterBase === 'data' ? Math.max(...years) : new Date().getFullYear();
    return { min: max - count + 1, max };
  }

  updateVisibleColumns(): void {
    const editableProps = this.editablePropsSet;
    const yearWindow = this.yearWindow();
    const expandedLevels = new Set<number>();
    this.flattenedRows.forEach((row) => {
      if (row.expanded) {
        expandedLevels.add(row.level);
      }
    });

    this.visibleColumns = this.data.headers
      .map((header: any, index: number) => {
        if (header.expandabile && index > 0 && !expandedLevels.has(index)) {
          return null;
        }

        // Latest-N-years filter: drop year-scoped value columns outside the
        // window; year-less columns are untouched.
        if (!header.expandabile && yearWindow) {
          const year = this.headerYear(header);
          if (year !== null && (year < yearWindow.min || year > yearWindow.max)) {
            return null;
          }
        }

        // Per UI Standards (Point 4): headers in Capital Case unless overridden
        const headerCase = (this.data?.textCase || 'capital') as any;
        const column: any = {
          name: formatDisplayText(header.name, headerCase),
          // Pivoted measures: the measure name spans its column values in the
          // group band above the header; `fullName` is the unambiguous label.
          group: header.group ? formatDisplayText(header.group, headerCase) : '',
          fullName: header.fullName ? formatDisplayText(header.fullName, headerCase) : undefined,
          prop: header.prop,
          width: header.width || undefined,
          type: header.type || undefined,
          // Per-column round-off override (e.g. Net Quantity = 0 decimals while
          // the rest of the table stays at the shared "Decimal places" setting).
          decimals: typeof header.decimals === 'number' ? header.decimals : undefined,
          // 'fraction' = the raw value is a 0-1 ratio (0.05 = 5%) and needs
          // ×100 before the "%" — e.g. Margin % / SSA %, unlike a column
          // already stored in percentage points (e.g. Cost Increase).
          percentScale: header.percentScale || undefined,
          // Blanks a negative percentage value in both data and total rows.
          hideNegative: header.hideNegative === true,
          infoIcon: header.infoIcon || false,
          infoText: header.infoText || '',
          alignment: header.alignment || 'start',
          frozenLeft: header.frozenLeft || false,
        };

        // Build CSS classes: alignment + optional partition line divider
        const classes: string[] = [];
        if (header.alignment) {
          classes.push(`text-${header.alignment}`);
        }
        // Table partition lines (UI Standards Point 5)
        if (this.showDivider(header.prop)) {
          classes.push('column-divider');
        }
        if (classes.length > 0) {
          column.headerClass = classes.join(' ');
          column.cellClass = classes.join(' ');
        }

        column.headerTemplate = this.infoIconHeader;

        if (header.expandabile) {
          column.cellTemplate = this.treeCell;
          column.levelIndex = index;
          // Single-column tree mode: every level renders in this one column,
          // indented by depth, instead of one column per level.
          column.indentByLevel = header.indentByLevel || false;
        }

        // An editable measure column renders through the input cell (editing
        // wins over a configured visual — a cell can't be both); otherwise a
        // visual rule (data bar / heat map / badge / trend / sparkline / …)
        // renders through the rich visual cell; else the plain
        // percentage/number cell.
        if (!header.expandabile && editableProps.has(String(header.prop))) {
          column.editable = true;
          column.cellTemplate = this.editCell;
        } else if (this.visualKind(header.prop)) {
          column.cellTemplate = this.visualCell;
        } else if (header.type === 'percentage') {
          column.cellTemplate = this.percentageCell;
        } else if (header.type === 'number') {
          column.cellTemplate = this.numberCell;
        }

        return column;
      })
      .filter(Boolean);

    // Freeze panes: pin the first N VISIBLE columns. ngx-datatable renders
    // `frozenLeft` columns in its sticky left group (header + body), so this
    // flag alone pins them; the custom Total row and the group band mirror it
    // through totalCellStyle / bandCellStyle. A header may also carry its own
    // `frozenLeft` (never cleared here).
    const frozenCount = this.frozenColumnCount;
    if (frozenCount > 0) {
      this.visibleColumns.forEach((col: any, index: number) => {
        if (index < frozenCount) col.frozenLeft = true;
      });
    }

    this.totalColumnWidth = this.visibleColumns.reduce((sum, col) => sum + (col.width || 0), 0);

    // Header row height: two lines when any label would otherwise be clipped.
    const wraps = this.visibleColumns.some((col: any, index: number) => {
      const measured = this.columnWidths[index];
      const width = measured != null && measured > 0 ? measured : (col.width || 130);
      return this.headerLabelWraps(col.name, width);
    });
    this.headerHeight = wraps ? DrillDownComponent.HEADER_TWO_LINE_HEIGHT : DrillDownComponent.HEADER_LINE_HEIGHT;

    // Group band: consecutive columns sharing a `group` merge into one cell;
    // every other column gets an empty spacer so the band lines up with the
    // header cells beneath it (widths are the measured header widths).
    const bands: Array<{ label: string; span: number; start: number }> = [];
    this.visibleColumns.forEach((col: any, index: number) => {
      const label = String(col.group || '');
      const last = bands[bands.length - 1];
      if (label && last && last.label === label) last.span += 1;
      else bands.push({ label, span: 1, start: index });
    });
    this.groupBands = bands;
    this.hasHeaderGroups = bands.some((band) => !!band.label);
  }

  /** Width of a group-band cell = the measured widths of the header cells it
   *  spans (same source as totalCellStyle, so the two rows never drift). */
  bandCellStyle(band: { span: number; start: number }): { [key: string]: string } {
    let width = 0;
    for (let i = band.start; i < band.start + band.span; i++) {
      const measured = this.columnWidths[i];
      width += measured != null && measured > 0 ? measured : (this.visibleColumns[i]?.width || 0);
    }
    const style: { [key: string]: string } = { flex: '0 0 auto', width: width > 0 ? `${width}px` : 'auto' };
    // A band cell sitting entirely over frozen columns pins with them (the
    // band div scrolls by scrollLeft, so sticky works inside it just like the
    // Total row's label cell). A band that straddles the freeze boundary is
    // left scrolling — pinning part of a merged cell isn't possible.
    if (band.start + band.span <= this.frozenColumnCount) {
      style['position'] = 'sticky';
      style['left'] = `${this.frozenLeftOffset(band.start)}px`;
      style['z-index'] = '2';
      // Own background (the band's colour) so the cells scrolling underneath
      // don't show through the pinned one.
      style['background'] = 'var(--dvm-drilldown-header-bg, #f8fafc)';
    }
    return style;
  }

  /** Sizes a total-row cell to the REAL rendered width of the header column
   *  at the same index (see columnWidths/measureColumnWidths) - not a
   *  recomputed approximation of ngx-datatable's own column-width algorithm,
   *  which differs by columnMode, column count, and container size, and is
   *  liable to drift from whatever ngx-datatable is actually doing on
   *  screen. Copying the true measured value is the only way this is
   *  guaranteed to line up, in every mode, on every resize. Falls back to
   *  the configured width only for the brief instant before the first
   *  measurement lands. */
  totalCellStyle(index: number, col: any): { [key: string]: string } {
    const measured = this.columnWidths[index];
    const width = measured != null && measured > 0 ? measured : col?.width;
    const style: { [key: string]: string } = { flex: '0 0 auto', width: width != null ? `${width}px` : 'auto' };
    // Frozen columns: the total cell pins at the same cumulative offset as the
    // body cell above it (the `.total-cell-frozen` class supplies the sticky
    // position / background; the offset has to be computed per column).
    if (this.isFrozenColumn(index)) style['left'] = `${this.frozenLeftOffset(index)}px`;
    return style;
  }

  setTotal(total: any, level: number): any {
    const totalRow: any = {
      name: 'Total',
      [this.nameProp]: 'Total',
      level,
      isTotal: true,
    };

    const numericHeaders = this.data.headers.filter((h: any) => !h.expandabile && !h.showTotal);

    numericHeaders.forEach((h: any) => {
      const prop = h.prop;
      const value = total?.[prop];

      if (value != null && !isNaN(value)) {
        if (h.type === 'number') {
          const decimals = typeof h.decimals === 'number' ? Math.max(0, Math.min(6, Math.floor(h.decimals))) : this.numberDecimals;
          totalRow[prop] = value.toFixed(decimals);
        } else if (h.type === 'percentage') {
          const decimals = typeof h.decimals === 'number' ? Math.max(0, Math.min(6, Math.floor(h.decimals))) : this.numberDecimals;
          totalRow[prop] = (h.percentScale === 'fraction' ? value * 100 : value).toFixed(decimals);
        } else {
          totalRow[prop] = value;
        }
      } else {
        totalRow[prop] = null;
      }
    });
    return totalRow;
  }

  getShowTotalLevels(): number[] {
    return this.data?.headers
      ?.map((h: any, index: number) => (h.expandabile && index !== 0 ? index + 1 : null))
      ?.filter((i: any) => i !== null);
  }

  toProp(name: string) {
    const header = this.data.headers.find((h: any) => h.name === name);
    return header?.prop;
  }

  toggleSet(set: Set<string>, key: string): void {
    if (set.has(key)) set.delete(key);
    else set.add(key);
  }

  setKey(set: Set<string>, key: string, condition: boolean): void {
    if (condition) set.add(key);
    else set.delete(key);
  }

  toggleArray(arr: string[], value: string): void {
    const index = arr.indexOf(value);
    if (index > -1) arr.splice(index, 1);
    else arr.push(value);
  }

  setArray(arr: string[], value: string, add: boolean): void {
    const index = arr.indexOf(value);
    if (add && index === -1) arr.push(value);
    else if (!add && index > -1) arr.splice(index, 1);
  }

  buildKey(row: any): string {
    const parts = [];
    if (row.parent?.parent) parts.push(row.parent.parent[this.nameProp]); // Customer
    if (row.parent && row.level >= 2) parts.push(row.parent[this.nameProp]); // Engine Model
    parts.push(row[this.nameProp]); // Current row
    return parts.join('|');
  }

  onCellActivate(event: any) {
    if (event.type !== 'click') return;

    const columnName = event.column.name;
    const row = event.row;
    const key = this.buildKey(row);

    // --- CUSTOMER NAME LEVEL ---
    if (columnName === 'Customer Name' && row.level === 1) {
      const ems = row.children || [];

      // Collect all Customer Spec keys under this Customer Name
      const allSpecKeys: string[] = [];
      ems.forEach((em: any) => {
        const emKey = `${key}|${em.name}`;
        (em.children || []).forEach((spec: any) => {
          allSpecKeys.push(`${emKey}|${spec.name}`);
        });
      });

      // If all specs selected -> deselect, else select all
      const allSelected = allSpecKeys.every((specKey) => this.customerSpecKeys.has(specKey));

      this.setKey(this.customerNameKeys, key, !allSelected);

      ems.forEach((em: any) => {
        const emKey = `${key}|${em.name}`;
        this.setKey(this.engineModelKeys, emKey, !allSelected);
        (em.children || []).forEach((spec: any) => {
          const specKey = `${emKey}|${spec.name}`;
          this.setKey(this.customerSpecKeys, specKey, !allSelected);
          this.setArray(this.customerSpec, spec.name, !allSelected);
        });
      });
    }

    // --- ENGINE MODEL LEVEL ---
    if (columnName === 'Engine Model' && row.level === 2) {
      const specs = row.children || [];
      const specKeys = specs.map((s: any) => `${key}|${s.name}`);

      // If all specs selected -> deselect, else select all
      const allSelected = specKeys.every((k: string) => this.customerSpecKeys.has(k));

      this.setKey(this.engineModelKeys, key, !allSelected);

      specKeys.forEach((specKey: string, i: number) => {
        this.setKey(this.customerSpecKeys, specKey, !allSelected);
        this.setArray(this.customerSpec, specs[i].name, !allSelected);
      });

      // Update Customer Name selection based on all Engine Models
      const parentKey = this.buildKey(row.parent);
      const allEMSelected = row.parent.children.every((em: any) =>
        this.engineModelKeys.has(`${parentKey}|${em.name}`),
      );
      this.setKey(this.customerNameKeys, parentKey, allEMSelected);
    }

    // --- CUSTOMER SPEC LEVEL ---
    if (columnName === 'Customer Spec' && row.level === 3) {
      this.toggleSet(this.customerSpecKeys, key);
      this.toggleArray(this.customerSpec, row.name);

      const emKey = this.buildKey(row.parent);
      const allSpecsSelected = row.parent.children.every((spec: any) =>
        this.customerSpecKeys.has(`${emKey}|${spec.name}`),
      );
      this.setKey(this.engineModelKeys, emKey, allSpecsSelected);

      const custKey = this.buildKey(row.parent?.parent);
      const allEMSelected = row.parent.parent.children.every((em: any) =>
        this.engineModelKeys.has(`${custKey}|${em.name}`),
      );
      this.setKey(this.customerNameKeys, custKey, allEMSelected);
    }

    // Refresh visual highlights
    this.updateHighlightedCells();

    // Update global filter for customer spec
    this.globalFilterService.updateFilterSelection(
      'customer_spec',
      this.customerSpec,
      false,
      this.widgetId,
      true,
    );
  }

  updateHighlightedCells(): void {
    this.highlightedCells = [];
    this.flattenedRows.forEach((row, idx) => {
      const key = this.buildKey(row);
      if (
        (row.level === 1 && this.customerNameKeys.has(key)) ||
        (row.level === 2 && this.engineModelKeys.has(key)) ||
        (row.level === 3 && this.customerSpecKeys.has(key))
      ) {
        this.highlightedCells.push(`${idx}-name`);
      }
    });
  }

  hasNavigation(levelIndex: number): boolean {
    return this.data.navigation?.some((nav: any) => nav.level === levelIndex);
  }

  onNavigate(navItem: any, row: any): void {
    const queryParams: any = {};
    if (row?.name && navItem.param) {
      queryParams[navItem.param] = row.name;
    }
    const queryString = new URLSearchParams(queryParams).toString();
    const path = navItem.path.startsWith('/') ? navItem.path.substring(1) : navItem.path;
    const fullUrl = `${location.origin}/${path}?${queryString}`;
    window.open(fullUrl, '_blank');
  }

  /**
   * Configurable column partition lines (UI Standards Point 5).
   *   data.showColumnDividers = true        → show on ALL columns
   *   data.dividerColumns = ['col1']        → show only after specified columns
   */
  showDivider(columnProp: string): boolean {
    const selective = this.data?.dividerColumns;
    if (Array.isArray(selective) && selective.length > 0) {
      return selective.includes(columnProp);
    }
    return this.data?.showColumnDividers === true;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}

